import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { PressList, Contact, Client, Hit } from '@pf/shared';
import {
  useClient,
  useClientStats,
  useClientActivity,
  useClientCommunications,
  useMarkClientMessageRead,
  useClientReport,
  useDownloadClientReport,
  type ActivityEntry,
  type ClientMessage,
  type ClientReportHitsByMonth,
  type ClientReportTopPublication,
  type ClientReportTopReporter,
} from '../hooks/useClients';
import { usePressLists, useCreatePressList, type CreatePressListBody } from '../hooks/usePressLists';
import { useContacts } from '../hooks/useContacts';
import { useHits } from '../hooks/useHits';

// ── Helpers ────────────────────────────────────────────────────────────────────

function clientInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase();
}

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Stats card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
}

// ── Create press list modal ────────────────────────────────────────────────────

function CreatePressListModal({
  clientId,
  clientName,
  onClose,
  onSuccess,
}: {
  clientId: string;
  clientName: string;
  onClose: () => void;
  onSuccess: (id: string) => void;
}) {
  const createList = useCreatePressList();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const body: CreatePressListBody = { name: name.trim(), clientId };
      if (description.trim()) body.description = description.trim();
      const result = await createList.mutateAsync(body);
      onSuccess((result as unknown as { pressList: PressList }).pressList?.id ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create list');
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">New Press List</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <p className="text-sm text-gray-500">
            This list will be scoped to <span className="font-medium text-gray-700">{clientName}</span>.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Tech Media Q2 2026"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || createList.isPending}
              className="bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 px-4 py-2 rounded-md text-sm font-medium"
            >
              {createList.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Activity feed ──────────────────────────────────────────────────────────────

function ActivityFeed({ clientId }: { clientId: string }) {
  const { data, isLoading } = useClientActivity(clientId);
  const entries = data?.activity ?? [];

  if (isLoading) return <p className="text-sm text-gray-400 py-4">Loading activity...</p>;
  if (entries.length === 0) return <p className="text-sm text-gray-400 py-4">No activity yet.</p>;

  return (
    <ul className="divide-y divide-gray-100">
      {entries.map((entry: ActivityEntry) => (
        <li key={`${entry.type}-${entry.id}`} className="py-3 flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0">
            {entry.type === 'message_sent' ? (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100">
                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </span>
            ) : (
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green-100">
                <svg className="w-3.5 h-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            {entry.type === 'message_sent' ? (
              <p className="text-sm text-gray-900">
                Pitch sent to <span className="font-medium">{entry.toEmail}</span>
                {entry.pressListName && (
                  <span className="text-gray-500"> via {entry.pressListName}</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-gray-900">
                <span className="font-medium">
                  {entry.contactFirstName} {entry.contactLastName}
                </span>{' '}
                added to{' '}
                {entry.pressListName ? (
                  <span className="text-gray-700">{entry.pressListName}</span>
                ) : (
                  'a press list'
                )}
              </p>
            )}
            {entry.subject && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{entry.subject}</p>
            )}
          </div>
          <time className="text-xs text-gray-400 flex-shrink-0">
            {relativeTime(entry.timestamp)}
          </time>
        </li>
      ))}
    </ul>
  );
}

// ── Tab components ─────────────────────────────────────────────────────────────

function PressListsTab({
  clientId,
  clientName,
  onNavigate,
}: {
  clientId: string;
  clientName: string;
  onNavigate: (path: string) => void;
}) {
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading } = usePressLists({ clientId });
  const lists = (data as unknown as { pressLists: PressList[] } | undefined)?.pressLists ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{lists.length} press list{lists.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Press List
        </button>
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading...</p>}
      {!isLoading && lists.length === 0 && (
        <p className="text-sm text-gray-400">No press lists yet. Create one to get started.</p>
      )}
      {!isLoading && lists.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {lists.map((list) => (
            <li key={list.id} className="py-3 flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <button
                  onClick={() => onNavigate(`/press-lists/${list.id}`)}
                  className="text-sm font-medium text-blue-600 hover:underline truncate"
                >
                  {list.name}
                </button>
                {list.description && (
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{list.description}</p>
                )}
              </div>
              <span className="ml-4 text-xs text-gray-400 flex-shrink-0">
                {list.contactsCount} contact{list.contactsCount !== 1 ? 's' : ''}
              </span>
            </li>
          ))}
        </ul>
      )}

      {showCreate && (
        <CreatePressListModal
          clientId={clientId}
          clientName={clientName}
          onClose={() => setShowCreate(false)}
          onSuccess={(id) => {
            setShowCreate(false);
            onNavigate(`/press-lists/${id}`);
          }}
        />
      )}
    </div>
  );
}

function ContactsTab({
  clientId,
}: {
  clientId: string;
}) {
  const { data, isLoading } = useContacts({ clientId, limit: 50 });
  const contacts = data?.data ?? [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>
        <Link
          to={`/contacts?clientId=${clientId}`}
          className="text-sm font-medium text-blue-600 hover:underline"
        >
          View all contacts →
        </Link>
      </div>

      {isLoading && <p className="text-sm text-gray-400">Loading...</p>}
      {!isLoading && contacts.length === 0 && (
        <p className="text-sm text-gray-400">No contacts associated with this client yet.</p>
      )}
      {!isLoading && contacts.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {(contacts as Contact[]).map((contact) => (
            <li key={contact.id} className="py-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-medium text-gray-600">
                  {(contact.firstName[0] ?? '').toUpperCase()}{(contact.lastName[0] ?? '').toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/contacts/${contact.id}`}
                  className="text-sm font-medium text-gray-900 hover:text-blue-600"
                >
                  {contact.firstName} {contact.lastName}
                </Link>
                {(contact.title || contact.company) && (
                  <p className="text-xs text-gray-500 truncate">
                    {[contact.title, contact.company].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              {contact.email && (
                <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">{contact.email}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Communications tab ─────────────────────────────────────────────────────────

function CommunicationsTab({ clientId }: { clientId: string }) {
  const { data, isLoading } = useClientCommunications(clientId);
  const markRead = useMarkClientMessageRead();
  const messages = data?.messages ?? [];

  if (isLoading) return <p className="text-sm text-gray-400 py-4">Loading communications...</p>;
  if (messages.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-gray-500">No client communications synced yet.</p>
        <p className="text-xs text-gray-400 mt-1">
          Email threads with stakeholders listed in this client's contact emails will appear here automatically.
        </p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-100">
      {messages.map((msg: ClientMessage) => (
        <li
          key={msg.id}
          className={[
            'py-4 flex items-start gap-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded',
            !msg.readAt ? 'bg-blue-50/40' : '',
          ].join(' ')}
          onClick={() => {
            if (!msg.readAt) markRead.mutate({ clientId, messageId: msg.id });
          }}
        >
          <div className="mt-0.5 flex-shrink-0">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-purple-100">
              <svg className="w-3.5 h-3.5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {!msg.readAt && (
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
              )}
              <p className="text-sm font-medium text-gray-900 truncate">
                {msg.subject ?? '(no subject)'}
              </p>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">
              From: <span className="font-medium">{msg.sender}</span>
              {msg.recipient && msg.recipient !== msg.sender && (
                <> · To: {msg.recipient}</>
              )}
            </p>
            {msg.bodySnippet && (
              <p className="text-xs text-gray-400 mt-0.5 truncate">{msg.bodySnippet}</p>
            )}
          </div>
          <time className="text-xs text-gray-400 flex-shrink-0">
            {relativeTime(msg.createdAt)}
          </time>
        </li>
      ))}
    </ul>
  );
}

// ── Coverage tab ───────────────────────────────────────────────────────────────

function formatHitDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function CoverageTab({ clientId }: { clientId: string }) {
  const { data, isLoading } = useHits({ clientId, limit: 10 });
  const hits = data?.hits ?? [];
  const total = data?.total ?? 0;

  if (isLoading) return <p className="text-sm text-gray-400 py-4">Loading coverage...</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{total} hit{total !== 1 ? 's' : ''}</p>
        <Link
          to={`/hits?clientId=${clientId}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Log Coverage
        </Link>
      </div>

      {hits.length === 0 && (
        <p className="text-sm text-gray-400">No coverage logged yet for this client.</p>
      )}

      {hits.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {(hits as Hit[]).map((hit) => (
            <li key={hit.id} className="py-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <a
                  href={hit.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:underline line-clamp-1"
                >
                  {hit.title}
                </a>
                <p className="text-xs text-gray-500 mt-0.5">{hit.publicationName}</p>
              </div>
              <time className="text-xs text-gray-400 flex-shrink-0">{formatHitDate(hit.publishedAt)}</time>
            </li>
          ))}
        </ul>
      )}

      {total > 10 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <Link
            to={`/hits?clientId=${clientId}`}
            className="text-sm font-medium text-blue-600 hover:underline"
          >
            View all {total} hits →
          </Link>
        </div>
      )}
    </div>
  );
}

// ── Report tab ─────────────────────────────────────────────────────────────────

function ReportTab({ clientId, clientName }: { clientId: string; clientName: string }) {
  const { data, isLoading } = useClientReport(clientId);
  const download = useDownloadClientReport();
  const [downloading, setDownloading] = React.useState(false);

  async function handleExport() {
    setDownloading(true);
    try {
      await download.mutateAsync({ clientId, clientName });
    } finally {
      setDownloading(false);
    }
  }

  if (isLoading) return <p className="text-sm text-gray-400 py-4">Loading report...</p>;
  if (!data) return <p className="text-sm text-gray-400 py-4">No report data available.</p>;

  const { total_hits, hits_by_month, top_publications, top_reporters, outreach_summary } = data;

  return (
    <div className="space-y-8">
      {/* Summary stats */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Summary</h3>
          <button
            onClick={handleExport}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {downloading ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total Hits" value={total_hits} />
          <StatCard label="Emails Sent" value={outreach_summary.messages_sent} />
          <StatCard label="Replies" value={outreach_summary.replies} />
          <StatCard
            label="Reply Rate"
            value={`${Math.round((outreach_summary.reply_rate ?? 0) * 100)}%`}
          />
        </div>
      </div>

      {/* Hits by month */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Hits by Month</h3>
        {hits_by_month.length === 0 ? (
          <p className="text-sm text-gray-400">No hits logged in the last 12 months.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Month</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Hits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(hits_by_month as ClientReportHitsByMonth[]).map((row) => (
                  <tr key={row.month}>
                    <td className="py-2 pr-4 text-gray-900">{row.month}</td>
                    <td className="py-2 text-right font-medium text-gray-900">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top publications & top reporters side by side */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Top Publications</h3>
          {top_publications.length === 0 ? (
            <p className="text-sm text-gray-400">No publications yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Publication</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Hits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(top_publications as ClientReportTopPublication[]).map((row) => (
                  <tr key={row.publicationName}>
                    <td className="py-2 pr-4 text-gray-900 truncate max-w-[180px]">{row.publicationName}</td>
                    <td className="py-2 text-right font-medium text-gray-900">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Top Reporters</h3>
          {top_reporters.length === 0 ? (
            <p className="text-sm text-gray-400">No reporters linked to hits yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 pr-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Reporter</th>
                  <th className="text-right py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Hits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(top_reporters as ClientReportTopReporter[]).map((row) => (
                  <tr key={row.contactId}>
                    <td className="py-2 pr-4 text-gray-900">
                      <Link
                        to={`/contacts/${row.contactId}`}
                        className="hover:text-blue-600 hover:underline"
                      >
                        {row.firstName} {row.lastName}
                      </Link>
                    </td>
                    <td className="py-2 text-right font-medium text-gray-900">{row.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

type TabKey = 'press-lists' | 'contacts' | 'coverage' | 'activity' | 'communications' | 'report';

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('press-lists');

  const { data, isLoading, isError } = useClient(id);
  const { data: statsData } = useClientStats(id);

  const client = (data as unknown as { client: Client } | undefined)?.client;
  const stats = statsData;

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  if (isError || !client) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Failed to load client.</p>
        <button
          onClick={() => navigate('/contacts')}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          Back to home
        </button>
      </div>
    );
  }

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'press-lists', label: 'Press Lists' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'coverage', label: 'Coverage' },
    { key: 'communications', label: 'Communications' },
    { key: 'activity', label: 'Activity' },
    { key: 'report', label: 'Report' },
  ];

  return (
    <div className="p-6 max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-900 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-900 font-medium truncate">{client.name}</span>
      </div>

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start gap-4">
          {client.logoUrl ? (
            <img
              src={client.logoUrl}
              alt={client.name}
              className="w-16 h-16 rounded-lg object-contain border border-gray-100 flex-shrink-0"
            />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-bold text-indigo-600">{clientInitials(client.name)}</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold text-gray-900">{client.name}</h1>
              {client.archivedAt && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                  Archived
                </span>
              )}
            </div>
            {client.description && (
              <p className="text-sm text-gray-600 mt-1">{client.description}</p>
            )}
            {client.website && (
              <a
                href={client.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline mt-1 inline-block"
              >
                {client.website.replace(/^https?:\/\//, '')}
              </a>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Messages Sent" value={stats?.messages_sent ?? 0} />
          <StatCard label="Replies" value={stats?.replies ?? 0} />
          <StatCard
            label="Reply Rate"
            value={stats ? `${Math.round((stats.reply_rate ?? 0) * 100)}%` : '0%'}
          />
          <StatCard label="Coverage" value={stats?.coverage_count ?? 0} />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={[
                  'px-5 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                ].join(' ')}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'press-lists' && id && (
            <PressListsTab
              clientId={id}
              clientName={client.name}
              onNavigate={navigate}
            />
          )}
          {activeTab === 'contacts' && id && (
            <ContactsTab clientId={id} />
          )}
          {activeTab === 'coverage' && id && (
            <CoverageTab clientId={id} />
          )}
          {activeTab === 'communications' && id && (
            <CommunicationsTab clientId={id} />
          )}
          {activeTab === 'activity' && id && (
            <ActivityFeed clientId={id} />
          )}
          {activeTab === 'report' && id && (
            <ReportTab clientId={id} clientName={client.name} />
          )}
        </div>
      </div>
    </div>
  );
}
